/**
 * Branch pipelines in the sidebar (owner 2026-10-15).
 *
 * The server (`platform/tabs` → `_buildBranchPipelineGroups`) sends one group
 * per branch pipeline the user can reach, right after STAGES, each carrying
 * `pipelineId` + `branchIds`. STAGES itself holds the default pipeline and
 * says (`defaultPipelineInScope`) whether that pipeline belongs in this user's
 * menu at all.
 *
 * Which pipelines the sidebar shows follows the header branch picker:
 *   - no branch picked     → every pipeline the user has access to (the default
 *                            one only if they belong to a branch running it)
 *   - branches picked      → the pipelines those branches run ("No Branch"
 *                            counts as the default pipeline)
 *   - the page you're on   → its pipeline always stays in the menu
 * One pipeline on its own fills STAGES, headed with that pipeline's name;
 * several show as one section each.
 *
 * A tenant without branch pipelines gets no pipeline groups from the server,
 * so every function here returns its input untouched.
 */
export interface PipelineGroupLike {
  group: string;
  label: string;
  /** Heading shown in place of `label`; the accordion keeps keying on `label`. */
  title?: string;
  routes: any[];
  pipelineId?: number | null;
  branchIds?: number[];
  defaultPipelineInScope?: boolean;
}

export interface SidebarBranchContext {
  /** Applied header branch selection (ids as strings). */
  branchIds: string[];
  /** "No Branch" picked — those leads sit on the default pipeline. */
  noBranch: boolean;
  /** Stage id of the stage page being viewed, if any. */
  activeStageId: string | null;
}

export const isPipelineGroup = (g: PipelineGroupLike) => g.pipelineId != null;
const isStageRoute = (r: any) => r?.component === "Dynamics" && r?.stageId != null;

export const csv = (v: unknown): string[] =>
  typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];

/**
 * The branch whose pipeline the Leads page reads (filter drawer, "Pick stage",
 * chip labels), from its header branch selection:
 *   - ONE branch picked that runs its own pipeline → that branch;
 *   - nothing picked → the user's own branch pipeline when it is the only
 *     pipeline in their menu (every branch they belong to runs it — the same
 *     case where that pipeline takes over STAGES in the sidebar);
 *   - anything else → `undefined` (the default pipeline, as before).
 */
export function leadsPipelineBranchId(
  groups: PipelineGroupLike[],
  branchIdCsv: unknown,
  noBranch: boolean,
): number | undefined {
  const forks = groups.filter(isPipelineGroup);
  if (forks.length === 0) return undefined;

  const ids = csv(branchIdCsv);
  if (ids.length === 1) {
    const runsFork = forks.some((f) => (f.branchIds ?? []).some((id) => String(id) === ids[0]));
    return runsFork ? Number(ids[0]) : undefined;
  }
  if (ids.length > 1 || noBranch) return undefined;

  const core = groups.find((g) => g.group === "core");
  if (core?.defaultPipelineInScope !== false || forks.length !== 1) return undefined;
  const home = forks[0].branchIds?.[0];
  return home != null ? Number(home) : undefined;
}

/** The sidebar groups for the current branch context (see file note). */
export function applyBranchPipelineContext<G extends PipelineGroupLike>(
  groups: G[],
  ctx: SidebarBranchContext,
): G[] {
  const forks = groups.filter(isPipelineGroup);
  if (forks.length === 0) return groups;

  const core = groups.find((g) => g.group === "core");
  const visible = new Set<number>();
  let showDefault: boolean;

  if (ctx.branchIds.length > 0 || ctx.noBranch) {
    const picked = new Set(ctx.branchIds);
    const forkBranches = new Set<string>();
    for (const f of forks) {
      for (const id of f.branchIds ?? []) {
        forkBranches.add(String(id));
        if (picked.has(String(id))) visible.add(f.pipelineId as number);
      }
    }
    showDefault = ctx.noBranch || ctx.branchIds.some((id) => !forkBranches.has(id));
  } else {
    for (const f of forks) visible.add(f.pipelineId as number);
    showDefault = core?.defaultPipelineInScope !== false;
  }

  // The page being viewed never drops out of its own menu.
  if (ctx.activeStageId) {
    const sid = ctx.activeStageId;
    const owner = forks.find((f) => f.routes.some((r) => String(r?.stageId) === sid));
    if (owner) visible.add(owner.pipelineId as number);
    else if (core?.routes.some((r) => isStageRoute(r) && String(r.stageId) === sid)) {
      showDefault = true;
    }
  }
  // Never an empty stage menu.
  if (!showDefault && visible.size === 0) showDefault = true;

  const shownForks = forks.filter((f) => visible.has(f.pipelineId as number));
  const out: G[] = [];
  let forksPlaced = false;
  const placeForks = (list: G[]) => {
    out.push(...list);
    forksPlaced = true;
  };

  for (const g of groups) {
    if (isPipelineGroup(g)) {
      // Server puts them right after STAGES; without a STAGES group they keep
      // their own slot.
      if (!forksPlaced) placeForks(shownForks);
      continue;
    }
    if (g.group === "core") {
      // One branch pipeline on its own takes over STAGES, under its own name
      // ("BANGALORE PIPELINE", as admins see it) so it never reads as the main
      // pipeline. The label stays STAGES: the accordion's open state keys on it.
      const takeOver = !showDefault && shownForks.length === 1;
      const base = showDefault ? g.routes : g.routes.filter((r) => !isStageRoute(r));
      const routes = takeOver ? [...base, ...shownForks[0].routes] : base;
      if (routes.length > 0) {
        out.push(takeOver ? { ...g, title: shownForks[0].label, routes } : { ...g, routes });
      }
      placeForks(takeOver ? [] : shownForks);
      continue;
    }
    out.push(g);
  }
  return out;
}
